import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { MapPin, BadgeCheck, Lock, ArrowRight, Loader2 } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { PastilleEnLigne } from "@/components/PastilleEnLigne";
import { formatFCFA, formatDate, saveParrainage } from "@/lib/user";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const mediaUrl = (u) => {
  if (!u.startsWith("/api")) return u;
  // L'URL porte déjà un jeton de partage signé (valable 1 h) : on ne l'altère pas.
  return `${process.env.REACT_APP_BACKEND_URL}${u}`;
};

/** Page ouverte par le QR code : aperçu public, le numéro reste derrière l'abonnement. */
export default function AnnoncePartagee() {
  const { annonceId } = useParams();
  const [params] = useSearchParams();
  const [annonce, setAnnonce] = useState(null);
  const [erreur, setErreur] = useState(null);
  const parrain = params.get("parrain");

  // Parrainage : on retient le membre qui a partagé, pour le récompenser à l'inscription.
  useEffect(() => {
    saveParrainage(parrain, annonceId);
  }, [parrain, annonceId]);

  useEffect(() => {
    axios
      .get(`${API}/annonces/${annonceId}/partage`)
      .then((r) => setAnnonce(r.data))
      .catch(() => setErreur("Cette annonce n'est plus disponible."));
  }, [annonceId]);

  const cibleAcces = annonce?.categorie === "air" ? "/air-bengue" : "/abonnement";

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <Navbar />
      <main className="mx-auto max-w-3xl px-5 pb-24 pt-28" data-testid="annonce-partagee-page">
        {erreur && (
          <p className="text-sm font-semibold text-amber-700" data-testid="annonce-partagee-erreur">
            {erreur}
          </p>
        )}

        {!annonce && !erreur && (
          <p className="flex items-center gap-2 text-sm text-stone-500">
            <Loader2 size={16} className="animate-spin" /> Chargement de l'annonce…
          </p>
        )}

        {annonce && (
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}>
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#E05638]">
              {annonce.rubrique}
            </p>
            <h1 className="font-heading mt-3 text-4xl font-bold tracking-tight text-[#12100E] sm:text-5xl">
              {annonce.air?.titre || `Annonce de ${annonce.annonceur.firstname}`}
            </h1>

            <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-stone-600">
              <span className="flex items-center gap-1.5 font-semibold text-stone-900">
                {annonce.annonceur.firstname}
                {annonce.annonceur.verified && <BadgeCheck size={15} className="text-emerald-600" />}
              </span>
              <PastilleEnLigne enLigne={annonce.annonceur.en_ligne} avecTexte
                               testid="annonce-partagee-presence" />
              {(annonce.air?.ville || annonce.annonceur.city) && (
                <span className="flex items-center gap-1.5">
                  <MapPin size={14} className="text-stone-400" />
                  {annonce.air ? `${annonce.air.ville}, ${annonce.air.pays}` : annonce.annonceur.city}
                </span>
              )}
              {annonce.air?.tarif ? (
                <span className="rounded-full bg-[#12100E] px-3 py-1 text-xs font-bold text-white">
                  {formatFCFA(annonce.air.tarif)}
                </span>
              ) : null}
            </div>

            {annonce.photos?.length > 0 && (
              <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3"
                   data-testid="annonce-partagee-photos">
                {annonce.photos.map((p, i) => (
                  <img key={i} src={mediaUrl(p.url)} alt=""
                       className="aspect-[3/4] w-full rounded-2xl border border-[#E5DFD5] object-cover" />
                ))}
              </div>
            )}

            {annonce.texte && (
              <p className="mt-8 whitespace-pre-line text-base leading-relaxed text-stone-700">
                {annonce.texte}
              </p>
            )}

            <p className="mt-6 text-xs text-stone-400">
              Publiée le {formatDate(annonce.date_debut)}
            </p>

            <div className="mt-10 rounded-3xl border border-[#E5DFD5] bg-white p-7">
              <p className="flex items-center gap-2 text-sm font-bold text-stone-900">
                <Lock size={16} className="text-[#E05638]" /> Le numéro est réservé aux membres abonnés
              </p>
              <p className="mt-2 text-sm text-stone-600">
                Abonnez-vous à {annonce.rubrique} pour appeler ou écrire sur WhatsApp
                directement à {annonce.annonceur.firstname}.
              </p>
              <Link
                to={cibleAcces}
                data-testid="annonce-partagee-abonnement"
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#E05638] px-7 py-3 text-sm font-bold text-white transition-transform hover:-translate-y-0.5"
              >
                Voir les abonnements <ArrowRight size={16} />
              </Link>
            </div>
          </motion.div>
        )}
      </main>
      <Footer />
    </div>
  );
}
